import { ActivityForms, DefaultActivityFormsDefs } from './ActivityFormContext';
import type { ComponentLookupTable } from './ActivityFormContext';
import { ChildActivityFormsDefs } from './childActivityFormsDefs';
import StarValueForm from '@/components/activity/createActivity/StarValueForm';

/**
 * Wizard-config med steget "hur många stjärnor är aktiviteten värd?" (steg 4).
 *
 * Detta är den ENDA configen som ändrar wizardens STRUKTUR. Barn-configen
 * (ChildActivityFormsDefs) får bara röra presentation — se dess bevarandekrav — och
 * den här funktionen läggs därför OVANPÅ vald bas-config, inte i stället för den.
 *
 * Strukturändringarna, och varför de är säkra:
 *   - Duration.nextForm  Created → StarValue, och nextFormAction 'Create' FLYTTAS till
 *     StarValue. Aktiviteten skapas alltså ett steg senare, av samma NextButton-logik.
 *   - Duration.additionalForm (MoreOptions/'Details') FLYTTAS också till StarValue.
 *     MoreOptions har "Create activity" i sig, så låg genvägen kvar på steg 3 vore
 *     stjärnsteget överhoppbart och värdet hade tyst blivit grundvärdet. MoreOptions
 *     är fortfarande nåbart, ett steg senare, med allt innehåll intakt
 *     (recurrence/subtasks/bild/AI).
 *   - MoreOptions.logicalPreviousForm Duration → StarValue, så bakåtvägen följer
 *     den nya ordningen.
 *   - formId/component för de befintliga stegen rörs inte: AssistantButton renderas
 *     fortfarande för Title/Date/Duration (CreateActivityHeader:s assistantForms).
 *
 * @param base         config att bygga på (default- eller barn-presentationen).
 * @param nextStepText i18n-nyckel för knappen som leder TILL stjärnsteget. Barnvyn
 *                     skickar sin egen barn-copy; övriga får default-texten.
 */
export const withStarValueStep = (
  base: ComponentLookupTable,
  nextStepText = 'Next: Stars',
): ComponentLookupTable => ({
  ...base,
  // progressTotal måste sättas på ALLA steg som visar indikatorn — annars byter den
  // antal prickar mitt i wizarden (3 på steg 1-2, 4 på steg 3).
  Title: { ...base.Title, progressTotal: 4 },
  Date: { ...base.Date, progressTotal: 4 },
  Duration: {
    ...base.Duration,
    nextForm: ActivityForms.StarValue,
    nextFormText: nextStepText,
    nextFormAction: undefined,
    additionalForm: undefined,
    additionalFormText: undefined,
    progressTotal: 4,
  },
  StarValue: {
    component: StarValueForm,
    formId: 'StarValue',
    // Läses bara av MoreOptionsHeader (MoreOptions-stegen) — CreateActivityHeader
    // visar en hårdkodad rubrik. Sätts för att typen kräver den.
    formName: 'Stars',
    nextForm: ActivityForms.Created,
    // Bekräftelsetexten ärvs från basens sista steg: barn-configen säger "Klart!",
    // default "Create activity". Steget som skapar aktiviteten låter som förut.
    nextFormText: base.Duration.nextFormText,
    nextFormAction: 'Create',
    additionalForm: ActivityForms.MoreOptions,
    additionalFormText: base.Duration.additionalFormText ?? 'Details',
    progressValue: 4,
    progressTotal: 4,
    // Att välja värde är hela poängen med steget — inget förvalt värde, knappen
    // öppnas när barnet/tonåringen tryckt på ett antal stjärnor.
    isValid: false,
    logicalPreviousForm: ActivityForms.Duration,
  },
  MoreOptions: {
    ...base.MoreOptions,
    logicalPreviousForm: ActivityForms.StarValue,
  },
});

/** Handledarvariant (skapar åt barn/ungdom): default-presentation + stjärnvärdessteget. */
export const StarActivityFormsDefs: ComponentLookupTable =
  withStarValueStep(DefaultActivityFormsDefs);

/**
 * Ungdomsvariant: som ovan, men knappen till steget talar XP. Tonåringens vy uttrycker
 * aldrig ett värde i stjärnor (utils/xp.ts) — inte heller i vägen dit.
 */
export const TeenStarActivityFormsDefs: ComponentLookupTable = withStarValueStep(
  DefaultActivityFormsDefs,
  'Next: XP',
);

/** Barnvariant: barn-copy bevarad + stjärnvärdessteget. */
export const ChildStarActivityFormsDefs: ComponentLookupTable = withStarValueStep(
  ChildActivityFormsDefs,
  'child.wizard.nextStars',
);
